import React, {
  useState,
  useMemo,
  useRef,
  useCallback,
  useEffect,
  useLayoutEffect,
} from 'react'
import PropTypes from 'prop-types'
import { Map } from 'immutable'
import compose from 'recompose/compose'
import isFunction from 'lodash/isFunction'
import { connect as connectRedux } from 'react-redux'
import Button, { TYPES } from '../Button/index'
import Icon, { ICON_TYPES } from '../Icon.v2/index'
import UniqueId from '../UniqueId/index'
import {
  getClientOffset,
  calculateAnchors,
  getContainerWidth,
  getVisibleIndexes,
  NULL_CLIENT_OFFSET,
} from './utils'

const DRAG_MOMENTUM_SPEED = 120
const DRAG_MOMENTUM_DECAY = 0.96
const TRANSITION_DURATION = '300ms'
const TOUCH_TRANSITION_DURATION = '500ms'

const Carousel = (props) => {
  const {
    touchable,
    staticText,
    onRenderContent,
    onVisibilityChange,
    visibilityThreshold = 0.80,
    shouldScrollByDisplayCount = true,
    children,
  } = props
  const slides = React.Children.toArray(children) || []
  const slidesCount = slides.length
  const isDraggable = touchable
  const hasDragMomentum = touchable
  const transtitionDuration = touchable ? TOUCH_TRANSITION_DURATION : TRANSITION_DURATION

  const [slideAnchors, setSlideAnchors] = useState([])
  const [index, setIndexState] = useState({ left: 0, right: 0 })
  const [maxIndex, setMaxIndex] = useState(slidesCount - 1)
  const [isDragging, setIsDragging] = useState(false)
  const [visibleIndexes, setVisibleIndexes] = useState([])
  const [isVisible, setIsVisible] = useState(false)
  const [showControls, setShowControls] = useState(false)

  const getTranslateOffset = useCallback(
    (newIndex, newSlideAnchors = slideAnchors) => {
      const startIndex = newSlideAnchors[newIndex]
      const start = startIndex ? startIndex.start : null

      return start ? -1 * start : 0
    },
    [slideAnchors],
  )

  const translateOffset = useRef(() => getTranslateOffset(index.left))
  const momentumDebounceId = useRef()
  const transitionDebounceId = useRef()
  const areArrowsLocked = useRef(false)
  const currentDragSpeed = useRef(0)
  const resizeObserverRef = useRef()
  const containerRef = useRef(null)
  const slideContainerRef = useRef(null)
  const slidesOuterContainerRef = useRef(null)
  const touchStartRef = useRef(NULL_CLIENT_OFFSET)
  const touchEndRef = useRef(NULL_CLIENT_OFFSET)
  const isMomentum = useRef(false)
  const slidesRefsCache = useRef([])

  const slidesRefs = useMemo(() => {
    const prev = slidesRefsCache.current
    return Array(slidesCount)
      .fill(null)
      .map((_, i) => prev[i] || React.createRef())
  }, [slidesCount])

  useLayoutEffect(() => {
    slidesRefsCache.current = slidesRefs
  }, [slidesRefs])

  const minTabIndex = slideAnchors.length > 0 ? slideAnchors[0].index : 0
  const maxTabIndex = slideAnchors.length > 0
    ? slideAnchors[slideAnchors.length - 1].index
    : slides.length - 1

  const arrowScrollCount = shouldScrollByDisplayCount ? visibleIndexes.length : 1

  const minScrollX = useMemo(() => {
    const startIndex = slideAnchors[maxIndex]
    const start = startIndex ? startIndex.start : null

    return start ? -1 * start : 0
  }, [slideAnchors, maxIndex])

  const showLeftArrow = index.left !== 0
  const showRightArrow = index.left < maxIndex

  const itemClassName = 'row-v2__item row-v2__item--disable-scale'

  const getBoundIndex = useCallback(
    (newIndex, newMaxIndex = maxIndex) => Math.max(0, Math.min(newMaxIndex, newIndex)),
    [maxIndex],
  )

  const getScrollIndex = useCallback(
    (newTranslateOffset, newSlideAnchors = slideAnchors) => {
      const currentOffset = -1 * newTranslateOffset

      const newIndex = newSlideAnchors.reduce(
        (acc, { start, end, width }, i) => {
          if (currentOffset >= start) {
            acc.left = currentOffset >= start + width / 2 ? i + 1 : i
          }
          if (containerRef.current) {
            const containerWidth = getContainerWidth(containerRef.current)
            acc.right = currentOffset + containerWidth >= end
              ? i
              : acc.right
          } else {
            acc.right = slidesCount - 1
          }

          return acc
        },
        {
          left: 0,
          right: 0,
        },
      )

      return {
        left: getBoundIndex(newIndex.left),
        right: Math.max(Math.min(slidesCount - 1, newIndex.right), newIndex.left),
      }
    },
    [slidesCount, slideAnchors, getBoundIndex],
  )

  const setTranslateOffset = useCallback(
    ({ offset, idx }) => {
      requestAnimationFrame(() => {
        if (!slideContainerRef || !slideContainerRef.current) {
          return
        }

        const newIndex = !idx ? getScrollIndex(offset) : idx

        if (transitionDebounceId.current) {
          cancelAnimationFrame(transitionDebounceId.current)
        }

        if (isDragging) {
          slideContainerRef.current.style.transitionDuration = '0ms'
        }

        slideContainerRef.current.style.transform = `translate(${offset}px)`

        transitionDebounceId.current = requestAnimationFrame(() => {
          slideContainerRef.current.style.transitionDuration = transtitionDuration
        })

        translateOffset.current = offset
        setIndexState(newIndex)
        if (newIndex !== idx && !isDragging) {
          setVisibleIndexes(getVisibleIndexes(newIndex, slides))
        }
      })
    },
    [
      isDragging,
      slideAnchors,
      slideAnchors.length,
      getScrollIndex,
      setIndexState,
    ],
  )

  const onResize = () => {
    const newSlideAnchors = calculateAnchors(slidesRefs)
    if (newSlideAnchors.length) {
      const containerWidth = getContainerWidth(slideContainerRef.current)
      const lastEnd = newSlideAnchors[newSlideAnchors.length - 1].end
      const newMaxIndex = getBoundIndex(
        newSlideAnchors.findIndex(({ start }) => start + containerWidth >= lastEnd),
        newSlideAnchors.length - 1,
      )
      const newLeftIndex = getBoundIndex(index.left, newMaxIndex)
      const newTranslateOffset = getTranslateOffset(newLeftIndex, newSlideAnchors)
      const newScrollIndex = getScrollIndex(newTranslateOffset, newSlideAnchors)

      setIndexState(newScrollIndex)
      setSlideAnchors(newSlideAnchors)
      setMaxIndex(newMaxIndex)
      setTranslateOffset({ offset: newTranslateOffset, idx: newScrollIndex })
      setVisibleIndexes(getVisibleIndexes(newScrollIndex, slides))
    }
  }

  const onArrowClick = useCallback(
    (indexOffset, callback) => {
      if (!areArrowsLocked.current) {
        areArrowsLocked.current = true

        const boundIndexOffset = Math.min(arrowScrollCount, indexOffset)
        const newBoundIndex = getBoundIndex(index.left + boundIndexOffset)

        requestAnimationFrame(() => {
          slideContainerRef.current.style.transitionDuration = TRANSITION_DURATION

          if (newBoundIndex !== index.left) {
            const newTranslateOffset = getTranslateOffset(newBoundIndex)
            const newScrollIndex = getScrollIndex(newTranslateOffset)

            slideContainerRef.current.addEventListener(
              'transitionend',
              () => {
                if (slidesRefs[newScrollIndex.left] && slidesRefs[newScrollIndex.left].current) {
                  slidesRefs[newScrollIndex.left].current.firstChild.focus()
                }
                areArrowsLocked.current = false
              },
              { once: true },
            )

            requestAnimationFrame(() => {
              slideContainerRef.current.style.transform = `translate(${newTranslateOffset}px)`
            })

            translateOffset.current = newTranslateOffset

            setIndexState(newScrollIndex)
            setVisibleIndexes(getVisibleIndexes(newScrollIndex, slides))

            if (callback) {
              callback(newScrollIndex)
            }
          } else {
            areArrowsLocked.current = false
          }
        })
      }
    },
    [
      slideAnchors,
      index,
      slides,
      getScrollIndex,
      setTranslateOffset,
      getBoundIndex,
      setIndexState,
    ],
  )

  const onTouchStart = useCallback(
    (e) => {
      if (momentumDebounceId.current) {
        cancelAnimationFrame(momentumDebounceId.current)
      }

      if (areArrowsLocked.current || !isDraggable || (e.touches && e.touches.length > 1)) {
        return
      }

      isMomentum.current = false
      setIsDragging(true)

      const offset = getClientOffset(e)
      touchStartRef.current = offset
      touchEndRef.current = offset
    },
    [isDraggable, setIsDragging],
  )

  const onTouchMove = useCallback(
    (e) => {
      e.stopPropagation()

      if (areArrowsLocked.current || isMomentum.current || !isDraggable || !isDragging) {
        return
      }

      touchEndRef.current = getClientOffset(e)
      const deltaX = touchStartRef.current.x - touchEndRef.current.x
      const deltaY = touchStartRef.current.y - touchEndRef.current.y
      touchStartRef.current = touchEndRef.current

      // To prevent vertical scrolling when interacting with the row
      // only handle horizontal drag if the horizontal drag speed is greater
      // than the vertical drag speed
      if (Math.abs(deltaX) > Math.abs(deltaY)) {
        currentDragSpeed.current = deltaX
        if (deltaX !== 0) {
          setTranslateOffset({ offset: translateOffset.current - deltaX })
        }
        global.document.body.style.overflow = 'hidden'
      } else {
        global.document.body.removeAttribute('style')
      }
    },
    [isDraggable, isDragging, setTranslateOffset],
  )

  const onTouchEnd = useCallback(
    (e) => {
      if (momentumDebounceId.current) {
        cancelAnimationFrame(momentumDebounceId.current)
      }

      if (areArrowsLocked.current || !isDraggable || (e.touches && e.touches.length > 0)) {
        return
      }

      if (hasDragMomentum) {
        isMomentum.current = true

        const momentumFunc = (speed) => {
          currentDragSpeed.current = speed
          momentumDebounceId.current = requestAnimationFrame(() => {
            const newTranslateOffset = translateOffset.current - speed

            if (Math.abs(speed) <= 1 || newTranslateOffset >= 0
            || newTranslateOffset <= minScrollX) {
              isMomentum.current = false
              setIsDragging(false)
              currentDragSpeed.current = 0
            } else {
              setTranslateOffset({ offset: newTranslateOffset })
              momentumFunc(speed * DRAG_MOMENTUM_DECAY)
            }
          })
        }

        momentumFunc(
          currentDragSpeed.current < 0
            ? Math.max(currentDragSpeed.current, -DRAG_MOMENTUM_SPEED)
            : Math.min(currentDragSpeed.current, DRAG_MOMENTUM_SPEED),
        )
      } else {
        setIsDragging(false)
      }
      global.document.body.removeAttribute('style')
    },
    [
      hasDragMomentum,
      minScrollX,
      isDraggable,
      setIsDragging,
      setTranslateOffset,
    ],
  )

  const onSlideFocus = useCallback(
    (i) => (e) => {
      const isFirstFocus = !e.currentTarget.contains(e.relatedTarget)

      if (isFirstFocus && i >= minTabIndex && i <= maxTabIndex) {
        slidesOuterContainerRef.current.scrollLeft = 0

        const isEnabled = !areArrowsLocked.current && !(isDraggable && isDragging)

        if (isEnabled && (i < index.left || i > index.right)) {
          onArrowClick((i < index.left ? -1 : 1), () => {
            slidesOuterContainerRef.current.scrollLeft = 0

            slideContainerRef.current.addEventListener(
              'transitionend',
              () => {
                if (slidesRefs[i] && slidesRefs[i].current) {
                  slidesRefs[i].current.firstChild.focus()
                }
              },
              { once: true },
            )
          })
        }
      }
    },
    [
      index,
      index.left,
      index.right,
      isDragging,
      isDraggable,
      onArrowClick,
    ],
  )

  const onSlideKeyDown = useCallback(
    (e) => {
      if (areArrowsLocked.current || (isDraggable && isDragging)) {
        e.preventDefault()
        e.stopPropagation()
      }
    },
    [isDraggable, isDragging],
  )

  // Function to get the class name of the slide
  const getSlideClassName = useCallback((slideIndex) => {
    if (slides[slideIndex]?.props.isAnimating) {
      return `${itemClassName} row-v2__item--removing`
    }

    if (!touchable) {
      return visibleIndexes.includes(slideIndex)
        ? `${itemClassName} row-v2__item--visible`
        : `${itemClassName} row-v2__item--hidden`
    }

    return `${itemClassName} row-v2__item--visible`
  }, [slides, touchable, visibleIndexes, itemClassName])

  useLayoutEffect(() => {
    if (resizeObserverRef.current) {
      resizeObserverRef.current.disconnect()
    }

    resizeObserverRef.current = new ResizeObserver(() => onResize())
    resizeObserverRef.current.observe(containerRef.current)
    slidesRefs.forEach(({ current }) => resizeObserverRef.current.observe(current))

    // Wait for the DOM to be updated
    requestAnimationFrame(() => {
      onResize()
    })
  }, [slidesCount])

  useEffect(() => {
    Array.from(slideContainerRef.current.children).forEach((child, i) => {
      if (i < minTabIndex || i > maxTabIndex) {
        child.querySelectorAll('*').forEach((node) => {
          // eslint-disable-next-line no-param-reassign
          node.tabIndex = -1
        })
      }
    })
  }, [minTabIndex, maxTabIndex, slideContainerRef.current])

  useEffect(() => {
    if (!areArrowsLocked.current && !(isDraggable && isDragging)) {
      if (momentumDebounceId.current) {
        cancelAnimationFrame(momentumDebounceId.current)
      }

      const newTranslateOffset = getTranslateOffset(index.left)

      setTranslateOffset({ offset: newTranslateOffset })

      if (translateOffset.current !== newTranslateOffset) {
        const slideRef = slidesRefs[index.left]
        if (slideRef && slideRef.current) {
          slideRef.current.firstChild.focus()
        }
      }

      touchStartRef.current = NULL_CLIENT_OFFSET
      touchEndRef.current = NULL_CLIENT_OFFSET
    }
  }, [isDragging, isDraggable])

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true)
          observer.unobserve(containerRef.current)
        }
      },
      {
        root: null,
        rootMargin: '0px',
        threshold: visibilityThreshold,
      },
    )
    if (containerRef.current) {
      observer.observe(containerRef.current)
    }
    return () => {
      if (containerRef.current) {
        observer.unobserve(containerRef.current)
      }
    }
  }, [containerRef.current])

  useEffect(() => {
    if (isVisible && isFunction(onVisibilityChange)) {
      onVisibilityChange(isVisible)
    }
  }, [isVisible, onVisibilityChange])

  useEffect(() => {
    if (visibleIndexes.length && isFunction(onRenderContent)) {
      onRenderContent(visibleIndexes)
    }
  }, [visibleIndexes, onRenderContent])

  useEffect(() => {
    setShowControls(!touchable && visibleIndexes.length < slidesCount)
  }, [visibleIndexes.length, slidesCount, touchable])

  useEffect(() => {
    if (!touchable) {
      slidesRefs.forEach((ref, i) => {
        if (ref.current) {
          ref.current.setAttribute('class', getSlideClassName(i))
        }
      })
    }
  }, [visibleIndexes, touchable, slidesRefs, getSlideClassName])

  return (
    <div
      className="row-v2__carousel-wrapper"
      ref={containerRef}
    >
      <div
        className="row-v2__items-and-controls-container"
        onMouseLeave={onTouchEnd}
      >
        {showControls && showLeftArrow ? (
          <Button
            type={TYPES.LINK}
            className="row-v2__control row-v2__control--prev"
            onClick={() => onArrowClick(arrowScrollCount * -1)}
            aria-label={staticText.getIn(['data', 'viewMore'])}
            title={staticText.getIn(['data', 'viewMore'])}
          >
            <Icon type={ICON_TYPES.CHEVRON_LEFT} />
          </Button>
        ) : null}
        <div
          ref={slidesOuterContainerRef}
          className="row-v2__items-list-container"
        >
          <ul
            ref={slideContainerRef}
            className="row-v2__items-list"
            onTouchStart={onTouchStart}
            onTouchMove={onTouchMove}
            onTouchEnd={onTouchEnd}
            onTouchCancel={onTouchEnd}
            onMouseDown={onTouchStart}
            onMouseMove={onTouchMove}
            onMouseUp={onTouchEnd}
          >
            {slides.map((slide, i) => {
              const onRemoveFunction = slide.props.onRemove
              return (
                <li
                  className={getSlideClassName(i)}
                  ref={slidesRefs[i]}
                  key={i}
                  onFocus={onSlideFocus(i)}
                  onKeyDown={onSlideKeyDown}
                >
                  <div className="row-v2__item-inner row-v2__item-inner--no-shadow">
                    {React.cloneElement(slide, { onRemove: onRemoveFunction })}
                  </div>
                </li>
              )
            })}
          </ul>
        </div>
        {showControls && showRightArrow ? (
          <Button
            type={TYPES.LINK}
            className="row-v2__control row-v2__control--next"
            onClick={() => onArrowClick(arrowScrollCount)}
            aria-label={staticText.getIn(['data', 'viewMore'])}
            title={staticText.getIn(['data', 'viewMore'])}
          >
            <Icon type={ICON_TYPES.CHEVRON_RIGHT} />
          </Button>
        ) : null}
      </div>
    </div>
  )
}

Carousel.propTypes = {
  children: PropTypes.node.isRequired,
  onRenderContent: PropTypes.func,
  onVisibilityChange: PropTypes.func,
  visibilityThreshold: PropTypes.number,
  shouldScrollByDisplayCount: PropTypes.bool,
}

Carousel.defaultProps = {
  visibilityThreshold: 0.80,
  shouldScrollByDisplayCount: true,
}

export default compose(
  UniqueId('row'),
  connectRedux(
    (state) => ({
      staticText: state.staticText.getIn(['data', 'row'], Map()),
      touchable: state.app.getIn(['viewport', 'touchable']),
    }),
  ),
)(Carousel)
